import {
  Component,
  type FunctionComponent,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
} from 'react';

import * as Sentry from '@sentry/browser';

import VARIANTS from '../../constants/variants';
import { ICONS } from '../../icons/icon';
import Alert from '../../ui/alert/alert';

import styles from './error_boundary.module.css';

function refreshApp(): void {
  window.location.reload();
}

const ErrorMessage: FunctionComponent = () => (
  <Alert
    variant={VARIANTS.DANGER}
    className={styles.alert}
    actionIcon={ICONS.Refresh}
    onActionClick={refreshApp}
  >
    Oops! Something went wrong
    <div>
      <a
        href="https://app.iconik.io/help/support/"
        target="_blank"
        rel="noopener noreferrer"
      >
        Contact iconik support
      </a>
    </div>
  </Alert>
);

type Props = PropsWithChildren;

type State = {
  error?: ReactElement;
};

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {};
  }

  componentDidCatch(error: Error): void {
    this.setState({ error: <ErrorMessage /> });

    // We need this handling here, even if these errors look duplicated on dev builds,
    // they will fire only once on production, see https://github.com/getsentry/sentry-javascript/issues/1432

    // biome-ignore lint/suspicious/noConsole: error logging
    console.error(error);
    Sentry.withScope((scope) => {
      scope.setLevel('fatal');
      Sentry.captureException(error);
    });
  }

  render(): ReactNode {
    return this.state.error || this.props.children;
  }
}

export default ErrorBoundary;
